import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { prettyEnum } from "./beautyUi";

export default function BeautyCompare() {
  const [params] = useSearchParams();
  const a = params.get("a"), b = params.get("b");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!a || !b) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/beauty/compare?a=${a}&b=${b}`);
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load comparison.");
      } finally { setLoading(false); }
    })();
  }, [a, b]);

  if (loading) return <div className="max-w-5xl mx-auto p-16 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  if (!data) return (
    <div className="max-w-3xl mx-auto px-6 py-14 text-center">
      <p className="font-serif text-2xl">Pick two scans from your history to compare.</p>
      <Link to="/beauty/history" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] underline underline-offset-4">Open history</Link>
    </div>
  );

  const dateA = new Date(data.a.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  const dateB = new Date(data.b.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  const scoresA = data.a.analysis?.overall_scores || {};
  const scoresB = data.b.analysis?.overall_scores || {};
  const skinA = data.a.analysis?.skin?.concerns_scores || {};
  const skinB = data.b.analysis?.skin?.concerns_scores || {};

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="beauty-compare-page">
      <Link to="/beauty/history" className="text-[10px] uppercase tracking-[0.2em] flex items-center gap-1.5 text-muted-foreground">
        <ArrowLeft size={11} /> Back to history
      </Link>
      <h1 className="editorial-h1 mt-4">Compare scans.</h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        Left is your earlier scan · right is the newer one. Green means improvement.
      </p>

      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        <ScanColumn label="Earlier" date={dateA} scan={data.a} testId="compare-a" />
        <ScanColumn label="Newer"   date={dateB} scan={data.b} testId="compare-b" />
      </div>

      {/* Overall deltas */}
      <section className="mt-10">
        <p className="eyebrow">Overall health · deltas</p>
        <div className="mt-3 grid sm:grid-cols-3 gap-3">
          <DeltaCard label="Skin"  a={scoresA.skin_health}  b={scoresB.skin_health}  testId="delta-skin" />
          <DeltaCard label="Hair"  a={scoresA.hair_health}  b={scoresB.hair_health}  testId="delta-hair" />
          <DeltaCard label="Beard" a={scoresA.beard_health} b={scoresB.beard_health} testId="delta-beard" />
        </div>
      </section>

      {/* Concern deltas */}
      <section className="mt-10">
        <p className="eyebrow">Skin concerns · deltas</p>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(data.diff.skin || {}).map(([k, v]) => (
            <DeltaCard key={k} label={prettyEnum(k)} a={skinA[k]} b={skinB[k]} testId={`delta-${k}`} />
          ))}
        </div>
      </section>

      {/* Concerns added / resolved */}
      {(data.diff.concerns_added?.length > 0 || data.diff.concerns_resolved?.length > 0) && (
        <section className="mt-10 grid sm:grid-cols-2 gap-4">
          <div className="border border-border p-5">
            <p className="eyebrow text-destructive">New concerns</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(data.diff.concerns_added || []).length === 0 && <p className="text-sm text-muted-foreground">None — clean run.</p>}
              {(data.diff.concerns_added || []).map(c => (
                <span key={c} className="text-[10px] uppercase tracking-[0.16em] border border-destructive text-destructive px-2 py-1">{prettyEnum(c)}</span>
              ))}
            </div>
          </div>
          <div className="border border-border p-5">
            <p className="eyebrow text-accent">Resolved</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(data.diff.concerns_resolved || []).length === 0 && <p className="text-sm text-muted-foreground">Nothing dropped off yet.</p>}
              {(data.diff.concerns_resolved || []).map(c => (
                <span key={c} className="text-[10px] uppercase tracking-[0.16em] border border-accent text-accent px-2 py-1">{prettyEnum(c)}</span>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function ScanColumn({ label, date, scan, testId }) {
  const face = scan.analysis?.face || {};
  return (
    <div className="border border-border p-5" data-testid={testId}>
      <p className="eyebrow">{label} · {date}</p>
      <p className="font-serif text-2xl mt-1">{prettyEnum(face.face_shape)}</p>
      <p className="text-xs text-muted-foreground mt-1">Tone · {prettyEnum(face.skin_tone)} · Undertone · {prettyEnum(face.skin_undertone)}</p>
    </div>
  );
}

function DeltaCard({ label, a, b, testId }) {
  const va = Number(a) || 0, vb = Number(b) || 0;
  const d = vb - va;
  const Icon = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
  const tone = d > 0 ? "text-accent" : d < 0 ? "text-destructive" : "text-muted-foreground";
  return (
    <div className="border border-border p-4" data-testid={testId}>
      <p className="eyebrow">{label}</p>
      <div className="mt-2 flex items-baseline justify-between gap-2">
        <p className="text-sm text-muted-foreground">{va} → {vb}</p>
        <p className={`text-xs font-semibold flex items-center gap-1 ${tone}`}>
          <Icon size={13} /> {d > 0 ? `+${d}` : d}
        </p>
      </div>
    </div>
  );
}
